import { Link } from "react-router-dom";
import type { NavigationItem } from "@app/types/Navigation";
import { sectionNavigationItems } from "@util/constant";
import cx from "classnames";

import { navigationItems as defaultNavigationItems } from "./constant";

export type NavigationProps = {
  navigationItems?: NavigationItem[];
};

export default function Navigation({
  navigationItems = defaultNavigationItems,
}: NavigationProps): React.ReactElement {
  return (
    <nav aria-label="header-nav">
      <ul className={"flex"}>
        <MyBTNavItem />
        {navigationItems.map((item) => {
          return <NavItem item={item} key={item.key} />;
        })}
      </ul>
    </nav>
  );
}

type NavItemProps = {
  item: NavigationItem;
};

const navItemStyle = cx(
  "px-2 py-3 duration-300 leading-[22.4px]",
  "xl:px-3",
  "hover:opacity-75"
);

function MyBTNavItem() {
  const { link, label } = sectionNavigationItems.myBT;

  return (
    <li
      className={cx("hidden items-center font-poppins text-white", "xl:flex")}
    >
      <Link to={link} className={navItemStyle} reloadDocument>
        {label}
      </Link>
    </li>
  );
}

function NavItem({ item }: NavItemProps): React.ReactElement {
  const { label, link, children }: NavigationItem = item;
  const hasChildren = !!children && children.length > 0;

  return (
    <li className={cx("group relative flex font-poppins text-white")}>
      <Link
        role="link"
        to={link}
        className={cx("flex items-center uppercase", navItemStyle)}
        reloadDocument
      >
        {label}
        {hasChildren ? (
          <div className="ml-1 border-x-4 border-t-4 border-solid border-x-transparent"></div> // Chevron down icon
        ) : null}
      </Link>
      {hasChildren ? (
        <ul
          className={cx(
            "invisible absolute top-[50px] w-max min-w-[160px] bg-gray-150 py-2 opacity-0 shadow-2xl duration-300",
            "group-hover:visible group-hover:opacity-100",
            "before:absolute before:bottom-full before:left-5 before:block before:h-0 before:w-0 before:border-[11px] before:border-solid before:border-x-transparent before:border-b-[color:var(--digital-light-gray)] before:border-t-transparent" // Arrow on top of dropdown box pointing to the nav item text
          )}
        >
          {item.children?.map((dropdownItemKey: string) => {
            const dropdownItem = sectionNavigationItems[dropdownItemKey];

            return dropdownItem ? (
              <DropdownItem key={dropdownItemKey} item={dropdownItem} />
            ) : null;
          })}
        </ul>
      ) : null}
    </li>
  );
}

function DropdownItem({ item }: { item: NavigationItem }) {
  return (
    <li className="px-4 py-1 font-poppins text-gray-850" key={item.link}>
      <Link className="hover:underline" to={item.link} reloadDocument>
        {item.label}
      </Link>
    </li>
  );
}
